import * as ko from 'knockout'
import {Context, Router} from '@profiscience/knockout-contrib-router';
import {
    ChecklistCategoryDto,
    ChecklistDto,
    ChecklistItemDto,
    DataWrapperChecklistDto,
    myTripChecklistApi
} from "../../api/api-wrapper";
import {MenuViewModelContext} from "../menu/menu";
import {autobind, observableArray} from "knockout-decorators";
import globalState from "../../global-state";
import {postbox} from "../../components/util/postbox";
import 'knockout-sortablejs';

// TODO correct interface definition.
interface JQueryStatic {
    collapse(options?: any): JQueryStatic;
}

interface MoveItem {
    categoryId: number;
    item: ChecklistItemDto;
}

class ViewModelContext extends Context {
    $parent: MenuViewModelContext;
    checklist: ChecklistDto;
}

class ViewModel {

    public checklist: ChecklistDto;

    /**
     * The trip id.
     */
    public tripId: number;

    /**
     * A map with an observable array of checklist items for each category.
     */
    public categoryItemsMap: Map<number, KnockoutObservableArray<ChecklistItemDto>>

    /**
     * A map with the collapse state for each category.
     */
    public categoryCollapseStateMap: Map<number, KnockoutObservable<boolean>>

    /**
     * The checklist categories.
     */
    @observableArray({deep: false, expose: true})
    public categories: ChecklistCategoryDto[];

    /**
     * Flag to indicate whether a item is currently dragged / sorted.
     * The menu on bottom will be hidden to enable autoscroll of the page.
     */
    public dragging: KnockoutObservable<boolean>;

    /**
     * Constructor.
     * @param ctx
     */
    constructor(ctx: ViewModelContext) {
        this.tripId = ctx.checklist.tripId;
        this.checklist = ctx.checklist;
        this.categories = ctx.checklist.categories
            .sort((item1, item2) => item1.order - item2.order);
        this.categoryItemsMap = new Map<number, KnockoutObservableArray<ChecklistItemDto>>();
        this.categoryCollapseStateMap = new Map<number, KnockoutObservable<boolean>>();
        this.categories.forEach(category => {
            this.categoryItemsMap.set(category.id, this.initCategoryItems(category.id, ctx.checklist.items))
            this.categoryCollapseStateMap.set(category.id, ko.observable(true));
        });

        this.save = this.save.bind(this);
        this.dragging = ko.observable(false);
    }

    private initCategoryItems(categoryId: number, allItems: ChecklistItemDto[]): KnockoutObservableArray<ChecklistItemDto> {
        return ko.observableArray(
            allItems
                .filter(item => item.category == categoryId)
                .sort((item1, item2) => {
                    if (item1.checked !== item2.checked) {
                        return item1.checked ? 1 : -1;
                    }
                    return item1.order - item2.order;
                })
        );
    }


    /**
     * Get the items for a category.
     * @param categoryId
     */
    @autobind
    public categoryItems(categoryId: number): KnockoutObservableArray<ChecklistItemDto> {
        return this.categoryItemsMap.get(categoryId);
    }

    /**
     * Get the collapse state for a category.
     * @param categoryId
     */
    @autobind
    public categoryCollapseState(categoryId: number): KnockoutObservable<boolean> {
        return this.categoryCollapseStateMap.get(categoryId);
    }

    /**
     * Category drag start event handler.
     * Closes all other categories.
     */
    @autobind
    public categoryDragStart() {
        // TODO remove cast after correct interface definition.
        //(<any>$('.collapse')).collapse('hide');
        this.categoryCollapseStateMap.forEach((collapseState) => {
            collapseState(false)
        });
        this.onDragStart();
    }

    /**
     * Save the order of the categories and the order for the items for each category.
     */
    async save() {
        globalState.loading(true);

        // Move items to the new categories.
        // If the categoryId of the item does not match the key of the categoryItemsMap, the item was moved to new category.
        const itemsToMove: MoveItem[] = [];
        this.categoryItemsMap.forEach((categoryItems, categoryId) =>
            categoryItems().filter(item => item.category != categoryId).forEach(item => {
                itemsToMove.push({
                    categoryId: categoryId,
                    item: item
                });
            })
        );

        for (const moveItem of itemsToMove) {
            await this.moveItemToCategory(moveItem);
        }

        // update the sort order for categories and items
        const sortPromises: Promise<DataWrapperChecklistDto>[] = [];
        const orderedCategoryIds = {};
        this.categories.forEach((category, index) => {
            orderedCategoryIds[category.id] = (index + 1);

            const categoryItems = this.categoryItemsMap.get(category.id)();
            if (categoryItems && categoryItems.length > 0) {
                const orderedItemIds = {};
                categoryItems.forEach((item, index) =>
                    orderedItemIds[item.id] = (index + 1)
                );
                sortPromises.push(this.reorderCategoryItems(category, orderedItemIds));
            }
        });

        return Promise.all(sortPromises)
            .then(() =>
                myTripChecklistApi.reorderTripChecklistCategories(String(this.tripId), orderedCategoryIds))
            .then(() => {
                globalState.loading(false);

                return this.goToChecklist();
            })
            .catch(() => {
                postbox.addError('checklist.errors.failedToSort');
                globalState.loading(false);
            });
    }

    private reorderCategoryItems(category: ChecklistCategoryDto, orderedItemIds: {}) {
        return myTripChecklistApi
            .reorderTripChecklistCategoryItems(String(this.tripId), category.id, orderedItemIds)
            .catch(response => {
                if (response.status === 404) {
                    return null;
                } else {
                    throw response;
                }
            });
    }

    private moveItemToCategory(moveItem: MoveItem) {
        return myTripChecklistApi
            .moveTripCheckListItemToCategory(String(this.tripId), moveItem.item.id, moveItem.categoryId, {})
            .catch(response => {
                if (response.status === 404) {
                    return null;
                } else {
                    throw response;
                }
            });
    }

    /**
     * Cancel sorting.
     */
    public cancel() {
        return this.goToChecklist();
    }

    @autobind
    public onDragStart() {
        this.dragging(true);
    }

    @autobind
    public onDragEnd() {
        this.dragging(false);
    }

    /**
     * Routes to checklist page.
     */
    private goToChecklist() {
        return Router.update(`/reise/${this.tripId}/checkliste`, {
            push: true,
            force: false
        })
    }

}

export default <KnockoutLazyPageDefinition>{
    viewModel: ViewModel,
    template: require('./sort.html'),
    componentName: "sort",
    loader: (ctx: ViewModelContext) => {
        return myTripChecklistApi.getTripChecklist(globalState.currentTrip.id.toString()).then(
            checklist => ctx.checklist = checklist.data
        );
    }
};
